/** @jsxImportSource @emotion/react */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { InputDate } from '@grapecity/wijmo.input';
import { hasClass, addClass } from '@grapecity/wijmo';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import CustomGrid from 'components/grids/CustomGrid';
import { tabs } from 'components/layouts/Tab';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import {
  approveSetupResultRequest,
  findSetupResultRequest,
  findSetupResultRequestHistory,
  rejectSetupResultRequest,
  requestSetupResultRequest,
  reRequestSetupResultRequest,
} from 'apis/setup/SetupSchedule';
import { SetupScheduleCondition, SetupTargetStepScheduleRequest } from 'models/setup/SetupSchedule';
import useSessionStore from 'stores/useSessionStore';
import { useMessageBar } from 'hooks/useMessageBar';

type Props = {
  open: boolean;
  close: () => void;
  condition: {
    elmPrjId: string;
    excpId?: string;
  };
};

const SetupResultCompleteRequestPopup = ({ open, close, ...props }: Props) => {
  const { t } = useTranslation();
  const { ensolYn } = useSessionStore();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [condition, setCondition] = useState<SetupScheduleCondition>(props?.condition);
  const tabMenus = useMemo(() => {
    return [
      { id: 'REQ', name: t('setup.label.실적 완료 처리', '실적 완료 처리') },
      { id: 'HIST', name: t('setup.label.이력 보기', '이력 보기') },
    ];
  }, []);

  return (
    <CustomDialog
      title={String(t('setup.label.셋업 실적 완료 처리', '셋업 실적 완료 처리'))}
      size={'xl'}
      open={open}
      onClose={close}
    >
      <div css={tabs.wrap}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-result-request-tab-${idx}`}
              aria-controls={`setup-result-request-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <RequestGrid condition={condition} ensolYn={ensolYn} />
        ) : (
          <HistoryGrid condition={condition} ensolYn={ensolYn} />
        )}
      </TabPanel>
    </CustomDialog>
  );
};
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-result-request-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

const HistoryGrid = ({ condition }: any) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<SetupTargetStepScheduleRequest[]>([]);
  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
    })
  );

  useEffect(() => {
    if (condition) {
      handleSearch(condition);
    }
  }, [condition]);

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록'),
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록')
    );
  };

  const handleSearch = async (params) => {
    findSetupResultRequestHistory(params?.elmPrjId, params?.excpId).then((result) =>
      setRowData(result)
    );
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row]?.dataItem;

      if ('atulEndDt' === binding) {
        cell.ariaReadOnly = true; // readonly;
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col]?.binding;
    // 실제종료일
    if ('atulEndDt' === binding) {
      e.cancel = true;
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'eqclId',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 70,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        minWidth: 80,
        width: 110,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        minWidth: 80,
        width: 208,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 80,
        width: 130,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'prjSpCdNm',
        header: String(t('setup.label.단계', '단계')),
        minWidth: 80,
        width: 200,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'atulEndDt',
        header: String(t('setup.label.실제완료일', '실제완료일')),
        minWidth: 80,
        width: 107,
        align: 'left',
        cssClass: 'WijmoDate',
        editor: dateEditor.current,
        cellTemplate: (params) => (params.value ? dayjs(params.value).format('YYYY.MM.DD') : ''),
      },
      {
        binding: 'aprReqUserNm',
        header: String(t('setup.label.요청자', '요청자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aprProgStatCdNm',
        header: String(t('setup.label.상태', '상태')),
        minWidth: 80,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('setup.label.승인자', '승인자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'rmk',
        header: String(t('setup.label.결재 Comment', '결재 Comment')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
    ];
  }, []);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`setupResultCompleteRequestPopupHistoryGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={443}
        isSelector={false}
        allowDragging={false}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
    </>
  );
};

const RequestGrid = ({ condition, ensolYn }: any) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<SetupTargetStepScheduleRequest[]>([]);
  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
      max: new Date(),
    })
  );

  useEffect(() => {
    if (condition) {
      handleSearch(condition);
    }
  }, [condition]);

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록'),
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록')
    );
  };

  const handleSearch = (params) => {
    findSetupResultRequest(params?.elmPrjId, params?.excpId).then((result) => setRowData(result));
  };

  const layoutDefinition = useMemo(() => {
    const drawActionButtons = (params) => {
      if (params.value === 'REQ') {
        return `<button class="req">${String(t('setup.label.요청', '요청'))}</button>`;
      } else if (params.value === 'REJ') {
        return `<button class="re-req">${String(t('setup.label.재요청', '재요청'))}</button>`;
      } else {
        return params.item?.aprProgStatCdNm || '';
      }
    };
    return [
      {
        binding: 'eqclId',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 70,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        minWidth: 80,
        width: 130,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        minWidth: 80,
        width: 208,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 80,
        width: 130,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'prjSpCdNm',
        header: String(t('setup.label.단계', '단계')),
        minWidth: 80,
        width: 200,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'atulEndDt',
        header: String(t('setup.label.실제완료일', '실제완료일')),
        minWidth: 80,
        width: 107,
        align: 'left',
        cssClass: 'WijmoDate',
        editor: dateEditor.current,
        cellTemplate: (params) => (params.value ? dayjs(params.value).format('YYYY.MM.DD') : ''),
      },
      {
        binding: 'aprReqUserNm',
        header: String(t('setup.label.요청자', '요청자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        visible: ensolYn === 'Y',
      },
      {
        binding: 'approve',
        header: String(t('setup.label.승인', '승인')),
        minWidth: 60,
        width: 60,
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip',
        isReadOnly: true,
        visible: ensolYn === 'Y',
        cellTemplate: (params) =>
          params.item.aprProgStatCd === 'INP'
            ? `<button class="approve">${String(t('setup.label.승인', '승인'))}</button>`
            : params.item.aprProgStatCd === 'APV'
            ? params.item.aprProgStatCdNm
            : '',
      },
      {
        binding: 'reject',
        header: String(t('setup.label.반려', '반려')),
        minWidth: 60,
        width: 60,
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip',
        isReadOnly: true,
        visible: ensolYn === 'Y',
        cellTemplate: (params) =>
          params.item.aprProgStatCd === 'INP'
            ? `<button class="reject">${String(t('setup.label.반려', '반려'))}</button>`
            : params.item.aprProgStatCd === 'REJ'
            ? params.item.aprProgStatCdNm
            : '',
      },
      {
        binding: 'aprProgStatCd',
        header: String(t('setup.label.상태/요청', '상태/요청')),
        minWidth: 80,
        width: 89,
        align: 'center',
        isReadOnly: true,
        visible: ensolYn === 'N',
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: drawActionButtons,
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('setup.label.승인자', '승인자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        visible: ensolYn === 'N',
      },
      {
        binding: 'rmk',
        header: String(t('setup.label.결재 Comment', '결재 Comment')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: ensolYn === 'N',
      },
    ];
  }, [ensolYn]);

  const validateRequest = (items: SetupTargetStepScheduleRequest[], isSingle = true) => {
    if ((items || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return false;
    }
    let valid = [] as (string | undefined)[];
    // 엔솔인 경우 승인/반려 처리
    if (ensolYn === 'Y') {
      // 반려인 경우 결재 Comment 필수값
      valid = (items || []).map((o, idx) => {
        if (!o.rmk) {
          return `${!isSingle ? `[${idx + 1}행]` : ''}${t(
            'setup.label.결재 Comment를 입력해 주세요.',
            '결재 Comment를 입력해 주세요.'
          )}`;
        }
      });
    }
    // 협력사인 경우 요청 처리
    else {
      // 실제완료일 확인
      valid = (items || [])
        .map((o, idx) => {
          if (!o.atulEndDt) {
            return `${!isSingle ? `[${idx + 1}행] ` : ''}${t(
              'setup.label.실제완료일을 입력해 주세요.',
              '실제완료일을 입력해 주세요.'
            )}`;
          }
        })
        .filter((element) => element !== undefined);
    }
    if (valid.length) {
      openMessageBar({
        type: 'warning',
        content: valid.join('<br/>'),
      });
      return false;
    }
    return true;
  };

  const validateApproveReject = (
    items: SetupTargetStepScheduleRequest[],
    isReject = false,
    isSingle = true
  ) => {
    if ((items || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return false;
    }
    let valid = [] as (string | undefined)[];
    if (ensolYn === 'Y' && isReject) {
      // 반려인 경우 결재 Comment 필수값
      valid = (items || [])
        .map((o, idx) => {
          if (!o.rmk) {
            return `${!isSingle ? `[${idx + 1}행]` : ''}${t(
              'setup.label.결재 Comment를 입력해 주세요.',
              '결재 Comment를 입력해 주세요.'
            )}`;
          }
        })
        .filter((element) => element !== undefined);
    }
    if (valid.length) {
      openMessageBar({
        type: 'warning',
        content: valid.join('<br/>'),
      });
      return false;
    }
    return true;
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col]?.binding;
      const item = panel.rows[row]?.dataItem;
      if ('atulEndDt' === binding) {
        // [25.11.28] 재요청 시 바로 요청상태로 변경되므로 실제완료일 수정 가능
        cell.ariaReadOnly = item.aprProgStatCd === 'INP' || ensolYn === 'Y';
      }
      if ('rmk' === binding) {
        cell.ariaReadOnly = item.aprProgStatCd !== 'INP' || ensolYn === 'N';
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col]?.binding;
    const item = grid.rows[e.row]?.dataItem;
    if ('atulEndDt' === binding) {
      // [25.11.28] 재요청 시 바로 요청상태로 변경되므로 실제완료일 수정 가능
      if (item.aprProgStatCd === 'INP' || ensolYn === 'Y') {
        e.cancel = true;
      }
    }
    if ('rmk' === binding) {
      if (item.aprProgStatCd !== 'INP' || ensolYn === 'N') {
        e.cancel = true;
      }
    }
  });

  const onCellEditEnding = (grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row]?.dataItem;
    const binding = grid.columns[e.col]?.binding;

    if ('atulEndDt' === binding) {
      if (newVal) {
        const newDate = dayjs(newVal);
        if (!newDate.isValid()) {
          openMessageBar({
            type: 'error',
            content: t('setup.label.날짜를 정확히 입력해 주세요.', '날짜를 정확히 입력해 주세요.'),
          });
          e.cancel = true;
        }
      }
      data.atulEndDt = CommonUtil.toYmd(newVal ?? '') ?? '';
    }
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    // selector disabled 처리
    grid.formatItem.addHandler((s, e) => {
      /*if (e.panel.cellType === 2 && e.col === 1 && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }*/
      const item = s.rows[e.row]?.dataItem;
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (
            (item.aprProgStatCd !== 'REQ' && ensolYn === 'N') ||
            (item.aprProgStatCd !== 'INP' && ensolYn === 'Y')
          ) {
            // e.cell.removeChild(e.cell.firstChild);
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row]?.dataItem;
      const binding = grid.columns[ht.col]?.binding;

      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (
              (row?.dataItem.aprProgStatCd !== 'REQ' && ensolYn === 'N') ||
              (row?.dataItem.aprProgStatCd !== 'INP' && ensolYn === 'Y')
            ) {
              row.isSelected = false;
            }
          });
        });
      }

      if (e.target instanceof HTMLButtonElement) {
        if (ensolYn === 'N') {
          // 요청인 경우
          if (e.target.classList.contains('req')) {
            if (!validateRequest([item])) {
              return;
            }
            requestResultComplete([item]);
          }
          // 재요청인 경우
          else if (e.target.classList.contains('re-req')) {
            if (item.aprProgStatCd !== 'REJ') {
              openMessageBar({
                type: 'warning',
                content: t(
                  'setup.label.반려된 요청만 재요청 가능합니다.',
                  '반려된 요청만 재요청 가능합니다.'
                ),
              });
              return;
            }
            reRequestSetupResultRequest(item).then((res) => {
              if (res?.successOrNot === 'Y') {
                const data = res?.data;
                item.aprReqId = data?.aprReqId;
                item.aprProgStatCd = data?.aprProgStatCd;
                item.aprProgStatCdNm = data?.aprProgStatCdNm;
                item.aprAprvUserId = data?.aprAprvUserId;
                item.aprAprvUserNm = data?.aprAprvUserNm;
                item.rmk = data?.rmk;
                gridRef.current?.refresh();
              } else {
                openMessageBar({
                  type: 'error',
                  content:
                    res.data && typeof res.data === 'string'
                      ? res.data
                      : t(
                          'setup.label.재요청 처리 중 오류가 발생했습니다.',
                          '재요청 처리 중 오류가 발생했습니다.'
                        ),
                });
              }
            });
          }
        } else {
          // 승인인 경우
          if (e.target.classList.contains('approve')) {
            if (!validateApproveReject([item])) {
              return;
            }
            approveResultComplete([item]);
          }
          // 반려인 경우
          else if (e.target.classList.contains('reject')) {
            if (!validateApproveReject([item], true)) {
              return;
            }
            rejectResultComplete([item]);
          }
        }
      }
    });
  };

  /**
   * 일괄요청
   */
  const handleAllRequest = () => {
    if ((checkedItems || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.일괄 요청할 항목을 선택해 주세요.',
          '일괄 요청할 항목을 선택해 주세요.'
        ),
      });
      return;
    }
    if (!validateRequest(checkedItems, false)) {
      return;
    }
    requestResultComplete(checkedItems);
  };

  /**
   * 일괄승인
   */
  const handleAllApprove = () => {
    if ((checkedItems || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.일괄 승인할 항목을 선택해 주세요.',
          '일괄 승인할 항목을 선택해 주세요.'
        ),
      });
      return;
    }
    if (!validateApproveReject(checkedItems, false, false)) {
      return;
    }
    approveResultComplete(checkedItems);
  };

  /**
   * 실적완료 요청 처리
   * @param items
   */
  const requestResultComplete = (items: SetupTargetStepScheduleRequest[]) => {
    requestSetupResultRequest(
      (items || []).reduce(
        (acc, cur) => [
          ...acc,
          {
            ...cur,
            atulEndDt: CommonUtil.toYmd(cur.atulEndDt ?? '') ?? '',
          },
        ],
        [] as SetupTargetStepScheduleRequest[]
      )
    ).then((res) => {
      if (res?.successOrNot === 'Y') {
        const result = res?.data;
        gridRef.current.rows.forEach((r) => {
          const d = (result || []).filter((o) => o.aprReqId === r.dataItem.aprReqId);
          if (d.length) {
            r.dataItem.aprProgStatCd = d[0].aprProgStatCd;
            r.dataItem.aprProgStatCdNm = d[0].aprProgStatCdNm;
            r.dataItem.aprReqUserId = d[0].aprReqUserId;
            r.dataItem.aprReqUserNm = d[0].aprReqUserNm;
          }
        });
        gridRef.current.refresh();
      }
      openMessageBar({
        type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          res?.successOrNot === 'Y'
            ? t(
                'setup.label.승인진행중 상태로 변경되었습니다.',
                '승인진행중 상태로 변경되었습니다.'
              )
            : res.data && typeof res.data === 'string'
            ? res.data
            : t('setup.label.요청 중 오류가 발생했습니다.', '요청 중 오류가 발생했습니다.'),
      });
    });
  };

  /**
   * 실적완료 승인 처리
   * @param items
   */
  const approveResultComplete = (items: SetupTargetStepScheduleRequest[]) => {
    approveSetupResultRequest(items).then((res) => {
      if (res?.successOrNot === 'Y') {
        const result = res?.data;
        gridRef.current.rows.forEach((r) => {
          const d = (result || []).filter((o) => o.aprReqId === r.dataItem.aprReqId);
          if (d.length) {
            r.dataItem.aprProgStatCd = d[0].aprProgStatCd;
            r.dataItem.aprProgStatCdNm = d[0].aprProgStatCdNm;
            r.dataItem.aprAprvUserId = d[0].aprAprvUserId;
            r.dataItem.aprAprvUserNm = d[0].aprAprvUserNm;
          }
        });
        gridRef.current.refresh();
      }
      openMessageBar({
        type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          res?.successOrNot === 'Y'
            ? t('setup.label.승인 처리되었습니다.', '승인 처리되었습니다.')
            : res.data && typeof res.data === 'string'
            ? res.data
            : t('setup.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
      });
    });
  };

  /**
   * 실적완료 반려 처리
   * @param items
   */
  const rejectResultComplete = (items: SetupTargetStepScheduleRequest[]) => {
    rejectSetupResultRequest(items).then((res) => {
      if (res?.successOrNot === 'Y') {
        const result = res?.data;
        gridRef.current.rows.forEach((r) => {
          const d = (result || []).filter((o) => o.aprReqId === r.dataItem.aprReqId);
          if (d.length) {
            r.dataItem.aprProgStatCd = d[0].aprProgStatCd;
            r.dataItem.aprProgStatCdNm = d[0].aprProgStatCdNm;
            r.dataItem.aprAprvUserId = d[0].aprAprvUserId;
            r.dataItem.aprAprvUserNm = d[0].aprAprvUserNm;
          }
        });
        gridRef.current.refresh();
      }
      openMessageBar({
        type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          res?.successOrNot === 'Y'
            ? t('setup.label.반려 처리되었습니다.', '반려 처리되었습니다.')
            : res.data && typeof res.data === 'string'
            ? res.data
            : t('setup.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
      });
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {ensolYn === 'N' && (
            <Button
              css={IconButton.button}
              className="request"
              onClick={handleAllRequest}
              disableRipple
            >
              {String(t('setup.label.일괄 요청', '일괄 요청'))}
            </Button>
          )}
          {ensolYn === 'Y' && (
            <Button
              css={IconButton.button}
              className="request"
              onClick={handleAllApprove}
              disableRipple
            >
              {String(t('setup.label.일괄 승인', '일괄 승인'))}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`setupResultCompleteRequestPopupRequestGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={443}
        allowDragging={false}
        excludeFilter={['aprProgStatCd', 'approve', 'reject']}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
      />
    </>
  );
};

export default SetupResultCompleteRequestPopup;
